import { useEffect } from 'react';
import { getMountConfig } from '../mountConfig';
import { useSiteContextStore } from '../layout/useSiteContextStore';

/**
 * Resolve the siteContextService via AngularJS dependency injection.
 * The service is NOT on window — it's an AngularJS factory only accessible
 * through the injector.
 */
export function getSiteContextService(): {
  contexts?: Record<string, string>;
  setContext?: (context: string) => void;
} | null {
  try {
    const ng = (window as unknown as Record<string, unknown>).angular as
      | {
          element: (el: Element | Document) => {
            injector?: () => { get?: (name: string) => any };
          };
        }
      | undefined;
    if (!ng) return null;
    const injector = ng.element(document.body)?.injector?.();
    return injector?.get?.('siteContextService') ?? null;
  } catch {
    return null;
  }
}

/**
 * Sets the site context on mount. In embedded mode, bridges to AngularJS
 * siteContextService + SidebarController scope. In standalone mode, writes
 * only to the Zustand store (no AngularJS dependency).
 *
 * Usage: `useSiteContext('admin');`
 */
/**
 * Maps camelCase context names (the keys exposed on AngularJS
 * siteContextService.contexts) to the canonical lowercase values the sidebar
 * checks against. Some routes pass the camelCase key (`'workOrder'`,
 * `'companyDetails'`) while others pass the canonical form (`'workorder'`).
 * Embedded mode resolves this via the AngularJS service lookup; standalone
 * has no AngularJS service, so we mirror the map here.
 */
const CONTEXT_KEY_MAP: Record<string, string> = {
  workOrder: 'workorder',
  companyDetails: 'companyDetails',
  companyMaintenance: 'companyMaintenance',
  serviceArea: 'serviceArea',
  vendorManagement: 'vendorManagement',
  overallowables: 'overallowables',
  auditResults: 'auditresults',
  clientPricing: 'clientpricing',
  caseManagementManager: 'caseManagementManager',
};

function normalizeContext(name: string): string {
  return CONTEXT_KEY_MAP[name] ?? name;
}

export function useSiteContext(contextName: string): void {
  const setContext = useSiteContextStore((s) => s.setContext);
  const { mode } = getMountConfig();

  useEffect(() => {
    // Empty/nullish contextName = leave the current context untouched.
    // Lets callers gate the set on a runtime condition (e.g. presence of a
    // URL param) — mirrors legacy AngularJS routes that only assign context
    // under specific conditions instead of on every mount.
    if (!contextName) return;

    // Always write the canonical (sidebar-matching) value to the Zustand store.
    setContext(normalizeContext(contextName));

    // Only bridge to AngularJS in embedded mode
    if (mode === 'embedded') {
      // Get siteContextService via AngularJS injector (not from window)
      const siteContextService = getSiteContextService();

      // Resolve camelCase key (e.g. 'workOrder') to the actual context value ('workorder')
      const resolvedContext = siteContextService?.contexts?.[contextName] ?? contextName;

      siteContextService?.setContext?.(resolvedContext);

      // Directly set vm.sidebar on the SidebarController scope as a reliability fix
      const trySetSidebarDirectly = (attempts: number) => {
        const ng = (window as unknown as Record<string, unknown>).angular as
          | { element: (el: Element) => { scope: () => Record<string, any> } }
          | undefined;
        if (!ng || attempts > 10) return;

        const sidebarSection = document.querySelector(
          'section[ng-controller="SidebarController as vm"]'
        );
        if (sidebarSection) {
          try {
            const scope = ng.element(sidebarSection).scope();
            if (scope?.vm) {
              scope.vm.sidebar = resolvedContext;
              if (typeof scope.$applyAsync === 'function') {
                scope.$applyAsync();
              }
              return;
            }
          } catch {
            // Silently ignore — AngularJS may not be available
          }
        }
        setTimeout(() => trySetSidebarDirectly(attempts + 1), 100);
      };

      const trySetNavBarDirectly = (attempts: number) => {
        const ng = (window as unknown as Record<string, unknown>).angular as
          | { element: (el: Element) => { scope: () => Record<string, any> } }
          | undefined;
        if (!ng || attempts > 10) return;

        const navbarSection = document.querySelector(`div[ng-controller="SubnavController"]`);
        if (navbarSection) {
          try {
            const scope = ng.element(navbarSection).scope();
            if (scope) {
              scope.nav = resolvedContext;
              if (typeof scope.$applyAsync === 'function') {
                scope.$applyAsync();
              }
              return;
            }
          } catch {
            // Silently ignore — AngularJS may not be available
          }
        }
        setTimeout(() => trySetNavBarDirectly(attempts + 1), 100);
      };

      trySetSidebarDirectly(0);
      trySetNavBarDirectly(0);
    }
  }, [contextName, setContext, mode]);
}
